<script setup lang="ts">
interface Props {
  tournament: TTournament;
}

const props = defineProps<Props>();

const tournamentDetails = ref<TTournamentDetailed>();
const { loadTournamentInfo } = useSmarticoTournaments();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const remainingTimes = ref<Record<number, string>>({});

const filterTournamentNameTitle = computed(() => props.tournament.name);

const prizeAmount = computed(() =>
  props.tournament.prize_pool_short
    ? `${props.tournament.prize_pool_short}`
    : 'Misto',
);

// Torneio que ainda não começou pode não ter tabela de prêmios publicada.
// Nesse caso mostramos os 3 slots com `---` em vez de esconder a linha
// (senão o card fica com um buraco preto) ou de inventar valores.
const topPrizes = computed<string[]>(() => {
  const prizes = tournamentDetails.value?.prizes;

  if (prizes && prizes.length > 0) {
    return prizes.slice(0, 3).map((p) => {
      if (p.points !== undefined && p.points !== null) return `R$${p.points}`;
      if (p.name) return p.name;

      return '---';
    });
  }

  return ['---', '---', '---'];
});

const participantCount = computed(
  () =>
    tournamentDetails.value?.registration_count ??
    props.tournament.registration_count ??
    0,
);

const formattedParticipantCount = computed(() => {
  try {
    return new Intl.NumberFormat('pt-BR').format(participantCount.value);
  } catch {
    return String(participantCount.value);
  }
});

const tournamentImageUrl = computed(() => {
  if (import.meta.client && window.innerWidth < 768) {
    return props.tournament.image2_mobile || props.tournament.image2;
  }

  return props.tournament.image2 || props.tournament.image2_mobile;
});

const tournamentId = computed(
  () => props.tournament.instance_id ?? props.tournament.tournament_id ?? '',
);

// Conta até o INÍCIO — o ribbon da API ("DIÁRIO"/"PAYDAY") é o da edição
// futura e não vale aqui, por isso o texto é fixo.
const startTime = computed<number | null>(
  () =>
    props.tournament.start_time ?? tournamentDetails.value?.start_time ?? null,
);

function formatTimeRemaining(target: number): string {
  let diff = target - Date.now();

  if (diff <= 0) return '—';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(days)}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const startsIn = computed(() => {
  const instId = props.tournament.instance_id;
  const pre = instId !== undefined ? remainingTimes.value[instId] : undefined;

  if (pre) return pre;

  return startTime.value ? formatTimeRemaining(startTime.value) : '—';
});

function viewTournament() {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  navigateTo(localeLink('/vip/tournaments/' + tournamentId.value));
}

async function startTournamentDetailsLoading() {
  if (!props.tournament.instance_id) return;

  try {
    const details =
      import.meta.client && window?._smartico
        ? await window._smartico
            .vapi('EN')
            .getTournamentInstanceInfo(props.tournament.instance_id)
        : await loadTournamentInfo(props.tournament.instance_id);

    if (details) tournamentDetails.value = details;
  } catch (error) {
    console.error(
      `Error loading tournament details ${props.tournament.instance_id}:`,
      error,
    );
  }
}

let intervalId: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  startTournamentDetailsLoading();

  if (startTime.value) {
    intervalId = setInterval(() => {
      const target = startTime.value;

      if (target && target > Date.now()) {
        remainingTimes.value[props.tournament.instance_id] =
          formatTimeRemaining(target);
      }
    }, 1000);
  }
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
});
</script>

<template>
  <div class="cardWrapper">
    <div class="tournament-card max-w-xl mx-auto select-none w-full box-border">
      <div
        class="bg-[#010612] rounded-[7.4px] p-4 border border-solid border-[#1D2329] cursor-pointer hover:border-[#2D3339] transition-colors"
        @click="viewTournament"
      >
        <!-- Imagem do torneio: div de fundo atrás da imagem (2px maior) em vez de borda -->
        <div class="relative rounded-[7.4px] overflow-hidden z-10">
          <div
            class="absolute -top-[1px] -left-[1px] -right-[1px] -bottom-[1px] rounded-[7.4px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] z-0"
          ></div>

          <div class="relative rounded-[7.4px] z-10">
            <NuxtImg
              :alt="`Tournament-${tournamentId}`"
              class="w-full h-[110px] object-cover rounded-[7.4px] border-[0.65px] border-solid border-[#ff3400]"
              :draggable="false"
              :src="tournamentImageUrl"
            />

            <!-- Só o cadeado sobre a arte escurecida: a data fica no
                 contador do rodapé -->
            <div
              class="absolute inset-0 flex items-center justify-center rounded-[7.4px] bg-black/50 z-10"
            >
              <svg
                class="w-[24px] h-[27px] drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                fill="none"
                viewBox="0 0 21.6974 24.7971"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8487 3.09963C12.9894 3.09963 14.7233 4.83349 14.7233 6.97417V9.2989H6.97417V6.97417C6.97417 4.83349 8.70803 3.09963 10.8487 3.09963ZM3.87454 6.97417V9.2989H0V24.7971H21.6974V9.2989H17.8229V6.97417C17.8229 3.12385 14.699 0 10.8487 0C6.99839 0 3.87454 3.12385 3.87454 6.97417ZM12.3985 15.4982V18.5978V20.1476H9.2989V18.5978V15.4982V13.9483H12.3985V15.4982Z"
                  fill="white"
                />
              </svg>
            </div>

            <!-- Ribbon fixo: substitui o "DIÁRIO"/"PAYDAY" da API -->
            <div
              class="absolute -top-[1px] -left-[1px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] text-white text-[8px] font-semibold px-[7.4px] py-[3.7px] rounded-br-[7.4px] z-20"
            >
              EM BREVE
            </div>

            <!-- Torneio não iniciado costuma vir com registration_count 0 -->
            <div
              v-if="participantCount > 0"
              class="absolute -bottom-[1px] -right-[1px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] text-white text-[8px] font-semibold px-[7.4px] py-[3.7px] rounded-tl-[7.4px] flex items-center gap-1 shadow-lg z-20"
            >
              <NuxtIcon
                class="text-[10px] text-white"
                name="players"
              />
              <span>{{ formattedParticipantCount }}</span>
            </div>
          </div>
        </div>

        <!-- Conteúdo abaixo da imagem -->
        <div class="mt-3 text-center w-full mx-auto">
          <span
            class="block w-full text-white text-center text-[14.796px] font-bold leading-[19.235px] truncate overflow-ellipsis"
            >{{ filterTournamentNameTitle }}</span
          >

          <!-- Linha de prêmios -->
          <div class="mt-2 flex items-center justify-around gap-1">
            <template
              v-for="(p, idx) in topPrizes"
              :key="idx"
            >
              <div class="flex items-center gap-1 justify-center text-white">
                <NuxtImg
                  alt="Crown Icon"
                  class="h-[16px]"
                  :src="
                    getAssetsUrlForCDN(
                      `/assets/gamification/place-${idx + 1}.png`,
                    )
                  "
                />
                <div
                  class="text-[8px] font-semibold truncate overflow-ellipsis"
                  >{{ p }}</div
                >
              </div>
            </template>
          </div>

          <!-- Countdown até o início -->
          <div
            class="mt-2 flex items-center justify-center gap-1 px-2 py-1 rounded-[6.587px]"
            style="background: rgba(255, 255, 255, 0.05)"
          >
            <NuxtIcon
              class="text-[11px] text-white"
              name="tournament-single/clock"
            />
            <span class="text-white/60 text-[8px] font-normal">Começa em:</span>
            <span class="text-white text-[10px] font-bold tabular-nums">{{
              startsIn
            }}</span>
          </div>

          <div
            class="flex gap-2 mt-2 p-1 rounded-[7.4px]"
            style="background: rgba(255, 255, 255, 0.05)"
          >
            <div
              class="flex flex-col basis-1/2 justify-between items-center min-w-0 [background:rgba(0,201,255,0.10)] backdrop-blur-[6.5873823165893555px] px-2.5 py-1.5 rounded-[6.587px]"
            >
              <div class="text-white text-[7px] tracking-[0.632px] font-normal"
                >PRÊMIO TOTAL</div
              >
              <div
                class="text-[#00C9FF] font-black text-center text-[11px] tracking-[-0.632px] truncate w-full"
                >{{ prizeAmount }}</div
              >
            </div>
            <!-- Sem "PARTICIPAR": o opt-in ainda não está aberto -->
            <button
              class="btn--em-breve flex basis-1/2 w-full justify-center items-center gap-[3.699px] self-stretch px-[7.398px] py-0 rounded-[4.439px]"
              @click.stop="viewTournament"
            >
              <span
                class="text-white text-[10px] font-extrabold whitespace-nowrap"
                >VER TORNEIO</span
              >
              <NuxtIcon
                class="text-[12px] text-white"
                name="mission/button/participar"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Botão neutro — o laranja é reservado pro CTA de torneio já aberto */
.btn--em-breve {
  background-color: #020716;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

/* Sizing igual aos demais cards: full width no grid, slide fixo no swiper */
.afiliado-swiper__items .tournament-card {
  box-sizing: border-box;
  flex: 0 0 260px !important;
  min-width: 260px !important;
  width: 260px !important;
  max-width: 260px !important;
}

.cardWrapper {
  width: 100%;
  min-width: 260px;
  isolation: isolate;
  contain: layout style paint;
}

.afiliado-swiper__items .cardWrapper {
  box-sizing: border-box;
  flex: 0 0 260px !important;
  min-width: 260px !important;
  width: 260px !important;
  max-width: 260px !important;
}

@media (min-width: 640px) {
  .afiliado-swiper__items .cardWrapper {
    width: 260px;
  }
}
</style>
